<div class="fullcontainer pr-3">
  <div class='sheet'>
    <div class='titleContainer'>
      <h1 class='header'>Report - Stock on Hand</h1>
      <div>
        <button class='btn btn-secondary' routerLink='/warehousing/reports'>All Reports</button>
      </div>
    </div>

    <div class='row'>
      <div class='col-md-4'>
        <label>Warehouse</label>
        <select class='form-control' (change)='this.onWarehouseChanged($event.target.value)'>
          <option value='-1'>All warehouses</option>
          @for (item of this.warehouses; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
      <div class='col-md-4'>
        <label>Location</label>
        <!-- Locations are scoped by warehouseId and the same aisle/shelf can exist at two
             sites, so this stays disabled until a warehouse is chosen. -->
        <select class='form-control'
          [disabled]='this.searchFilters.warehouseId === -1'
          [(ngModel)]='this.searchFilters.warehouseLocationId'>
          <option [value]='-1'>All locations</option>
          @for (item of this.locations; track item.id) {
            <option [value]='item.id'>{{ item.aisle }} / {{ item.shelf }}</option>
          }
        </select>
      </div>
      <div class='col-md-4'>
        <label>Product</label>
        <select class='form-control' [(ngModel)]='this.searchFilters.productId'>
          <option [value]='-1'>All products</option>
          @for (item of this.products; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
    </div>
    <br />
    <br />
    <button class='btn btn-primary' (click)='this.loadReport()' [disabled]='this.running'>Run Report</button>
    <hr />
    <button class='btn btn-secondary' (click)='exportToCSV()' [disabled]='this.rows.length === 0'>Export to CSV</button>
  </div>

  <div class='sheet'>
    @if (this.running) {
      <p class='m-0'><i class="fas fa-spinner fa-spin"></i> Running&hellip;</p>
    }

    @if (this.hasRun && !this.running && this.rows.length === 0) {
      <p class='m-0 text-muted'>No stock levels matched those filters.</p>
    }

    @if (this.rows.length > 0) {
      <div class='titleContainer'>
        <h1 class='header'>Results</h1>
      </div>

      <h3>TOTAL QTY ON HAND: {{ this.getTotalQty() }}</h3>
      <p class='text-muted'>
        {{ this.rows.length }} level(s). Quantities are replayed from the ledger, not read from
        the stored level, so this report and the reconciliation report cannot disagree.
      </p>

      <!-- A negative level cannot happen legitimately, so it is called out above the
           table rather than left for someone to spot in a column of numbers. -->
      @if (this.getNegativeCount() > 0) {
        <div class='alert alert-danger'>
          <strong>{{ this.getNegativeCount() }} of these levels are negative.</strong>
          Stock cannot legitimately fall below zero, so the total above is understating
          real stock and the ledger needs reconciling.
          <button class='btn btn-sm btn-outline-dark ml-2' routerLink='/warehousing/reports/reconciliation'>
            Reconciliation report
          </button>
        </div>
      }

      <!-- Only reachable now that the report reads the ledger. On the old screen the stored
           level was the only number available, so a divergence could not be shown here at
           all and this report could contradict reconciliation without either saying so. -->
      @if (this.getDriftCount() > 0) {
        <div class='alert alert-danger'>
          <strong>{{ this.getDriftCount() }} row(s) disagree with the ledger.</strong>
          The stored level and the sum of the movements behind it are different numbers. The
          quantities below are the ledger's, which is the reliable one — the stored level is a
          cache of it. Both are shown per row so the size of the gap is visible.
          <button class='btn btn-sm btn-outline-dark ml-2' routerLink='/warehousing/reports/reconciliation'>
            Reconciliation report
          </button>
        </div>
      }

      <h3 class='mt-3'>VALUE AT COST: {{ this.formatMoney(this.valuation.value) }}</h3>
      <p class='text-muted'>
        across {{ this.formatQty(this.valuation.valuedUnits) }} unit(s)
        of {{ this.formatQty(this.valuation.unitsOnHand) }} on hand
      </p>

      <!-- The money never appears without its coverage. This report previously showed no value
           at all, which was at least not misleading; a bare total would have been worse. -->
      @if (this.valuationIncomplete) {
        <div class='alert alert-warning'>
          <strong>This value does not cover every unit above.</strong>
          <ul class='mb-0'>
            @if (this.valuation.unitsWithoutCostBasis > 0) {
              <li>
                {{ this.formatQty(this.valuation.unitsWithoutCostBasis) }} unit(s) have no cost
                basis — nothing that put them there carried a unit cost — and are excluded.
              </li>
            }
            @if (this.valuation.unitsAtZeroCost > 0) {
              <li>
                {{ this.formatQty(this.valuation.unitsAtZeroCost) }} unit(s) are priced at zero.
                Zero is deliberate for third-party stock a customer owns, and is also where a
                fully written-off product lands — check the product before reading it as either.
              </li>
            }
            @if (this.valuation.unitsAtNegativeCost > 0) {
              <li>
                {{ this.formatQty(this.valuation.unitsAtNegativeCost) }} unit(s) are at a
                <strong>negative cost</strong>, holding
                {{ this.formatMoney(this.valuation.negativeCostValueExcluded) }}. Kept out of the
                total rather than deducted from it. This is an error to correct.
              </li>
            }
            @if (this.valuation.unitsFromUncostedMovements > 0) {
              <li>
                {{ this.formatQty(this.valuation.unitsFromUncostedMovements) }} unit(s) were
                booked in on movements carrying no unit cost, so the average they sit behind
                rests on less stock than it appears to.
              </li>
            }
          </ul>
        </div>
      }

      <table class='table'>
        <thead>
          <th>Product</th>
          <th>SKU</th>
          <th>Warehouse</th>
          <th>Aisle</th>
          <th>Shelf</th>
          <th class='text-right'>Qty</th>
          <!-- Shown always, not only when it differs: a column that appears on a bad day is a
               column nobody knows to look for on a good one. -->
          <th class='text-right'>Stored Level</th>
          <th class='text-right'>Unit Cost</th>
          <th class='text-right'>Value</th>
          <th>Last Movement</th>
          <th></th>
        </thead>
        <tbody>
          @for (row of this.rows; track this.rowKey(row)) {
            <tr [class.table-danger]='this.isNegative(row) || this.hasDrifted(row)'>
              <td>{{ row.productName }}</td>
              <td>{{ row.sku }}</td>
              <td>{{ row.warehouseName }}</td>
              <td>{{ row.aisle }}</td>
              <td>{{ row.shelf }}</td>
              <td class='text-right'>{{ this.formatQty(row.ledgerQty) }}</td>
              <td class='text-right'>
                {{ this.formatQty(row.levelQty) }}
                @if (this.hasDrifted(row)) {
                  <span class='badge badge-danger ml-1'>differs</span>
                }
              </td>
              <td class='text-right'>{{ this.formatCost(row, row.unitCost) }}</td>
              <td class='text-right'>{{ this.formatCost(row, row.value) }}</td>
              <td>{{ this.formatDateTime(row.lastMovementAt) }}</td>
              <td>
                <button class='btn btn-warning'
                  routerLink='/warehousing/reports/movement-history'
                  [queryParams]='{productId: row.productId}'>Audit trail</button>
              </td>
            </tr>
          }
        </tbody>
      </table>

      <p class='text-muted mb-0'>
        <small>
          <strong>Not priced</strong> in a cost column means no amount was recorded. A product
          priced deliberately at nothing shows as £0.00.
        </small>
      </p>
    }
  </div>
</div>
